import { useHydrateStore } from '@oncolens/oncolens-ui/lib/hooks/common/useHydrateForm';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import { FaPlusCircle } from 'react-icons/fa';
import shallow from 'zustand/shallow';
import { riskFactorKeys } from './riskFactor.keys';
import { createRiskFactor, getRiskFactors } from '../hooks/useRiskFactor';
import useRequestForm from '../store';
import RiskFactorsDisplay from './RiskFactorsDisplay';
// Interfaces for interacting with data

export interface RiskFactorItem {
  id: string;
  description: string | undefined;
  value?: string;
  comments?: string;
}

// Interfaces for context provider

interface RiskFactorActiveIds {
  riskFactorId: string | null;
}

interface RiskFactorFormIds {
  episodeId: string | null;
}

interface RiskFactorContextValues {
  riskFactors: RiskFactorItem[];
  setRiskFactors: React.Dispatch<React.SetStateAction<RiskFactorItem[]>>;
  isInAutosave: boolean;
  activeIds: RiskFactorActiveIds;
  formIds: RiskFactorFormIds;
  setActiveIds: React.Dispatch<React.SetStateAction<RiskFactorActiveIds>>;
  persistToForm: (items: RiskFactorItem[]) => void;
}

const RiskFactorContext = React.createContext({
  riskFactors: [] as RiskFactorItem[],
  setRiskFactors: (
    state: RiskFactorItem[] | ((previousState: RiskFactorItem[]) => Record<string, unknown>)
  ) => {},
  isInAutosave: false,
  activeIds: {} as RiskFactorActiveIds,
  formIds: {} as RiskFactorFormIds,
  setActiveIds: (
    state: RiskFactorActiveIds | ((previousState: RiskFactorActiveIds) => Record<string, unknown>)
  ) => {},
  persistToForm: () => {},
} as RiskFactorContextValues);

export const useRiskFactorContext = () => React.useContext(RiskFactorContext);

interface RiskFactorsProps {
  episodeId: string;
  diseaseId: string;
  isEpisodeSaved: boolean;
  isInAutosave: boolean;
}

//****************
// ** component **
//****************
const RiskFactors = ({ diseaseId, episodeId, isEpisodeSaved, isInAutosave }: RiskFactorsProps) => {
  const formKey = `diseases.${diseaseId}.episodes.${episodeId}.riskFactors`;
  const [updateField, updated] = useRequestForm(
    (store) => [store.updateField, store.diseases[diseaseId].episodes[episodeId].riskFactors],
    shallow
  );
  const formIds = {
    episodeId,
  };
  const [riskFactorItems, setRiskFactorItems] = useState((updated || []) as RiskFactorItem[]);
  const [activeIds, setActiveIds] = useState({ riskFactorId: null } as RiskFactorActiveIds);
  // TODO form keys import
  const { isLoading, setIsHydrated } = useHydrateStore({
    apiCall: async () => getRiskFactors(episodeId),
    keys: { cacheKey: riskFactorKeys.lists(episodeId), formKey },
    shouldRun: isInAutosave && isEpisodeSaved && (!updated || (updated && updated.length === 0)),
    updateStore: updateField,
  });

  useEffect(() => {
    if (updated && !activeIds.riskFactorId) {
      setRiskFactorItems(updated);
    }
  }, [updated]);

  // rehydrate on cancerdetailid update
  useEffect(() => {
    setIsHydrated(false);
  }, [episodeId]);

  const contextValues = {
    riskFactors: riskFactorItems,
    setRiskFactors: setRiskFactorItems,
    formIds,
    isInAutosave,
    activeIds,
    setActiveIds,
    persistToForm: (values: RiskFactorItem[]) => {
      updateField(formKey, values, { markDirty: false });
    },
  };

  const handleAddRiskFactor = () => {
    const id = nanoid();

    contextValues.setRiskFactors((riskFactors) => [
      ...riskFactors.slice(),
      {
        id,
        description: undefined,
      },
    ]);

    contextValues.setActiveIds((previous) => ({ ...previous, riskFactorId: id }));
  };

  return (
    <RiskFactorContext.Provider value={contextValues}>
      {
        <>
          {contextValues.riskFactors.map((item) => (
            <RiskFactorsDisplay key={item.id} data={item} episodeId={episodeId} />
          ))}
          <button
            data-cy="add-risk-factor-button"
            type="button"
            className={clsx(
              'flex items-center justify-center w-full p-8 text-gray-600 transition-colors duration-200 bg-gray-100 border-2 border-gray-300 border-dashed rounded-md hover:bg-white hover:border-gray-400',
              contextValues.riskFactors.length > 0 && 'mt-3'
            )}
            onClick={() => {
              handleAddRiskFactor();
            }}
          >
            <FaPlusCircle className="mr-2" /> Add Risk Factor...
          </button>
        </>
      }
    </RiskFactorContext.Provider>
  );
};

RiskFactors.save = async (episodeId: string, riskFactors: any) => {
  return createRiskFactor(episodeId, riskFactors);
};

export { RiskFactors as default };
